import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IRequestInfo } from 'legacy/lut/classes/models/service'

import { IPartnerFlag, IPartnerFlags } from 'clickNgo/entities/partner/models'
import { IAttachment } from 'clickNgo/models/file'
import { partnerThunks } from 'onboarding/features/api/partnerThunks'
import {
  fetchPartnerByPartnerIdFulfilled,
  fetchPartnerByPartnerIdPending,
  fetchPartnerByPartnerIdRejected,
} from 'onboarding/features/reducers/partnerExtraReducer'

import { getNewPartnerById } from './api/getNewPartnerById'
import { getPartnerById } from './api/getPartnerById'
import { getPartnerFilesById } from './api/getPartnerFilesById'
import { getPartnerFlagsById } from './api/getPartnerFlagsById'
import { initialState } from './configs'
import { IPartnerData, IPartnerFlagsData } from './models'
import {
  getPartnerByIdFulfilled,
  getPartnerByIdPending,
  getPartnerByIdRejected,
} from './reducers/getPartnerByIdReducer'
import {
  getPartnerFilesByIdFulfilled,
  getPartnerFilesByIdPending,
  getPartnerFilesByIdRejected,
} from './reducers/getPartnerFilesByIdReducer'
import {
  getPartnerFlagsByIdFulfilled,
  getPartnerFlagsByIdPending,
  getPartnerFlagsByIdRejected,
} from './reducers/getPartnerFlagsByIdReducer'

const partnerSlice = createSlice({
  name: 'partner',
  initialState,
  reducers: {
    updatePartnerFlagData: (state, action: PayloadAction<IPartnerFlagsData>) => {
      state.partnerFlags = action.payload
    },
    updatePartnerProgramFlags: (state, action: PayloadAction<IPartnerFlag[]>) => {
      state.partnerFlags.program = action.payload
    },
    updatePartnerAccreditationFlags: (state, action: PayloadAction<IPartnerFlag[]>) => {
      state.partnerFlags.accreditation = action.payload
    },
    updatePartnerFlags: (state, action: PayloadAction<IPartnerFlags>) => {
      state.partnerFlags = {
        ...state.partnerFlags,
        ...action.payload,
      }
    },
    updatePartnerAttachments: (state, action: PayloadAction<IAttachment[]>) => {
      state.partnerFiles.data = action.payload
    },
    updatePartnerData: (state, action: PayloadAction<IPartnerData>) => {
      state.partnerData = action.payload
    },
    updatePartnerDataRequestInfo: (state, action: PayloadAction<IRequestInfo>) => {
      state.partnerData = { ...state.partnerData, ...action.payload }
    },
    resetPartnerData: () => {
      return initialState
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPartnerById.pending, getPartnerByIdPending)
      .addCase(getPartnerById.rejected, getPartnerByIdRejected)
      .addCase(getPartnerById.fulfilled, getPartnerByIdFulfilled)

      .addCase(getNewPartnerById.pending, getPartnerByIdPending)
      .addCase(getNewPartnerById.rejected, getPartnerByIdRejected)
      .addCase(getNewPartnerById.fulfilled, getPartnerByIdFulfilled)

      // Skip autocorrection of MainPhone in order to show to user the phone as it is saved in db,
      // to let him take actions to fix it. Without that user sees corrected phone, which in fact can be bounced on server validation,
      // in that case user will be confused, because corrected phone is shown.
      .addCase(partnerThunks.fetchOneByPartnerId.pending, fetchPartnerByPartnerIdPending)
      .addCase(partnerThunks.fetchOneByPartnerId.rejected, fetchPartnerByPartnerIdRejected)
      .addCase(partnerThunks.fetchOneByPartnerId.fulfilled, fetchPartnerByPartnerIdFulfilled)

      .addCase(partnerThunks.fetchChangeRequestByPartnerId.pending, fetchPartnerByPartnerIdPending)
      .addCase(
        partnerThunks.fetchChangeRequestByPartnerId.rejected,
        fetchPartnerByPartnerIdRejected
      )
      .addCase(
        partnerThunks.fetchChangeRequestByPartnerId.fulfilled,
        fetchPartnerByPartnerIdFulfilled
      )

      .addCase(getPartnerFilesById.pending, getPartnerFilesByIdPending)
      .addCase(getPartnerFilesById.rejected, getPartnerFilesByIdRejected)
      .addCase(getPartnerFilesById.fulfilled, getPartnerFilesByIdFulfilled)

      .addCase(getPartnerFlagsById.pending, getPartnerFlagsByIdPending)
      .addCase(getPartnerFlagsById.rejected, getPartnerFlagsByIdRejected)
      .addCase(getPartnerFlagsById.fulfilled, getPartnerFlagsByIdFulfilled)
  },
})

export const {
  updatePartnerDataRequestInfo,
  resetPartnerData,
  updatePartnerData,
  updatePartnerFlagData,
  updatePartnerAccreditationFlags,
  updatePartnerProgramFlags,
  updatePartnerAttachments,
  updatePartnerFlags,
} = partnerSlice.actions

export const partnerReducer = partnerSlice.reducer

import('./listeners')
